import * as THREE from 'three';
const { tan, cos, atan, acos, min, max, pow, PI, sqrt } = Math;
import decompose from '../utils/decompose';
import { getCamera, getCameraWorldTransform, getRect } from '../../modules/player';
import { get, getWorldTransform } from '../../modules/scene';
import { merge, virtualTargetId } from '../../modules/sceneGraph';
import updateCameraLookAtTarget from '../utils/updateCameraLookAtTarget';
import getProjectionMatrix from '../utils/getProjectionMatrix';
import getTargetWorldMatrix from '../utils/getTargetWorldMatrix';

const cameraProjectMatrix = new THREE.Matrix4();
const cameraUnprojectMatrix = new THREE.Matrix4();
const ndcReferencePoint = new THREE.Vector3();
const worldRelative = new THREE.Vector3();
const deltaReferencePoint = new THREE.Vector3();
const referencePoint = new THREE.Vector3();
const translationMatrix = new THREE.Matrix4();

const cameraPosition = new THREE.Vector3();
const targetPosition = new THREE.Vector3();
const offset = new THREE.Vector3();

export default function pan(store, translator) {
  function move({ deltaX, deltaY }) {
    let zoom = 1;
    if(__BROWSER__) zoom = window.devicePixelRatio;
    updatePan(store, new THREE.Vector2(deltaX / zoom, deltaY / zoom));
  }

  return {
    label: 'Pan',
    enabled: true,
    display: {
      playerTools: true,
    },
    options: {},

    tool: {
      drag: function (ev) {
        if (!this.active && ev.which !== 2 && ev.which !== 3 && !ev.shiftKey) return false;

        return {
          momentum: true,
          handle: function (ev) {
            move(ev);
          },
        };
      },

      swipe: function (ev) {
        move(ev);
      },
    },
  };
}

function updatePan(store, mouseDelta) {

  const cameraId = getCamera(store);
  const cameraData = get(store, { id:  cameraId, evalPlug: 'Camera' });
  const playerData = get(store, { scene: true, evalPlug: 'Player' });
  const targetWorldMatrix = getTargetWorldMatrix(store, cameraId);
  const cameraWorldMatrix = getWorldTransform(store, cameraId);
  const rect = getRect(store);
  var projectionMatrix = getProjectionMatrix(get(store, { id: cameraId, evalPlug: 'Camera' }));

  // convert mouse delta into relative delta in NDC.
  var relativeDelta = new THREE.Vector2().copy(mouseDelta).divide(rect).multiplyScalar(2.0);
  relativeDelta.x *= -1;

  const invCameraWorldMatrix = new THREE.Matrix4().getInverse(cameraWorldMatrix, true);
  cameraProjectMatrix.copy(projectionMatrix).multiply(invCameraWorldMatrix);

  ndcReferencePoint.setFromMatrixPosition(targetWorldMatrix).applyMatrix4(cameraProjectMatrix);
  cameraUnprojectMatrix.getInverse(cameraProjectMatrix, true);

  referencePoint.set(0, 0, ndcReferencePoint.z).applyMatrix4(cameraUnprojectMatrix);
  deltaReferencePoint.set(relativeDelta.x, relativeDelta.y , ndcReferencePoint.z).applyMatrix4(cameraUnprojectMatrix);

  // ensure that NDC has the same layout as mouseDelta coordinate space
  worldRelative.copy(deltaReferencePoint).sub(referencePoint);
  //bounding box constraining
  if (cameraData.radiusConstraint && worldRelative.length() > 0) {
    const { maxBoundingSphere, minBoundingSphere } = cameraData.radiusConstraint;
    cameraPosition.setFromMatrixPosition(cameraWorldMatrix);
    targetPosition.setFromMatrixPosition(targetWorldMatrix);

    const directionNorm = new THREE.Vector3().copy(worldRelative).normalize();
    const camRay = new THREE.Ray(cameraPosition, directionNorm);
    const targRay = new THREE.Ray(targetPosition, directionNorm);

    const targHit = targRay.intersectSphere(minBoundingSphere).sub(targetPosition).length() - __BOUNDING_BUFFER__;

    let inCamHit = camRay.intersectSphere(minBoundingSphere);
    if (inCamHit) inCamHit = inCamHit.sub(cameraPosition).length() - __BOUNDING_BUFFER__;
    else inCamHit = Infinity;

    const outCamHit = camRay.intersectSphere(maxBoundingSphere).sub(cameraPosition).length() - __BOUNDING_BUFFER__;

    const moveLength = Math.min(worldRelative.length(), targHit, inCamHit, outCamHit);
    worldRelative.normalize().multiplyScalar(moveLength);
  }

  translationMatrix.makeTranslation(worldRelative.x, worldRelative.y, worldRelative.z);
  offset.addVectors(cameraData.offset, worldRelative);
  cameraData.offset = offset;

  targetWorldMatrix.multiplyMatrices(translationMatrix, targetWorldMatrix);
  cameraWorldMatrix.multiplyMatrices(translationMatrix, cameraWorldMatrix);

  if (playerData.constraintCameraY && playerData.orbitMode !== 'trackball') {
    cameraPosition.setFromMatrixPosition(cameraWorldMatrix);
    targetPosition.setFromMatrixPosition(targetWorldMatrix);

    if (cameraPosition.y < 0) {
      targetPosition.y -= cameraPosition.y;
      cameraPosition.y = 0;
    }

    targetWorldMatrix.setPosition(targetPosition);
    cameraWorldMatrix.setPosition(cameraPosition);
  }

  updateCameraLookAtTarget(cameraWorldMatrix, targetWorldMatrix, cameraData.upPosition);

  store.dispatch(merge([cameraId, 'Transform', 0], decompose(cameraWorldMatrix)));
  store.dispatch(merge([virtualTargetId, 'Transform', 0], decompose(targetWorldMatrix)));
  //store.dispatch(merge([cameraId, 'Camera', 0], {offset}));
}



// WEBPACK FOOTER //
// ./src/translators/tools/pan.js